// Pointer-lock capture + ESC pause menu (D33). The camera reads movementX/Y, which only stay
// meaningful while the pointer is locked (cursor pinned center, hidden); without the lock the OS
// cursor wanders off-window and mouselook dies. Click captures while playing; the browser's ESC
// always exits the lock (unpreventable) → lockChange(false) → pause menu (Continue re-captures /
// Quit → main page). Pure state machine — every side-effect is injected so it's unit-testable.

// SUSPENDED (D44 P0-A/Item 9): the game shell parks pointer-lock while a full-screen overlay owns the
// cursor (the full-time EndScreen). We exit the lock so the buttons are clickable AND swallow the
// resulting pointerlockchange so the pause menu never pops over the end screen. Left on unsuspend().
export type PointerLockState = 'UNLOCKED' | 'LOCKED' | 'MENU' | 'FALLBACK' | 'SUSPENDED';

export interface PointerLockHooks {
  /** Lobby/overlay gate — capture only makes sense in live play. */
  isPlaying(): boolean;
  /** element.requestPointerLock() — must be called from a user-gesture handler (click qualifies). */
  requestLock(): void;
  /** document.exitPointerLock() — release the cursor (guarded for the missing-API case by the caller). */
  exitLock(): void;
  /** Show/hide the pause-menu overlay. */
  showMenu(open: boolean): void;
  /** Quit → back to the main page (navigation is the injected side-effect). */
  quit(): void;
}

export class PointerLockController {
  private s: PointerLockState = 'UNLOCKED';
  constructor(private readonly hooks: PointerLockHooks) {}

  get state(): PointerLockState {
    return this.s;
  }

  /** The mouselook gate: engaged when locked — or in FALLBACK, where a denied lock degrades to the
   *  legacy free-mouse behavior (broken controls are worse than a wandering cursor). */
  get mouselookEngaged(): boolean {
    return this.s === 'LOCKED' || this.s === 'FALLBACK';
  }

  /** Click on the game surface: capture the mouse (in live play; FALLBACK keeps retrying). */
  click(): void {
    if ((this.s === 'UNLOCKED' || this.s === 'FALLBACK') && this.hooks.isPlaying()) this.hooks.requestLock();
  }

  /** pointerlockchange. Gaining the lock engages mouselook; losing it while LOCKED = ESC → menu.
   *  A SUSPENDED shell swallows the unlock (the EndScreen owns the cursor — no pause menu over it). */
  lockChange(locked: boolean): void {
    if (locked) {
      if (this.s === 'SUSPENDED') return; // a stray re-lock while suspended is ignored
      this.s = 'LOCKED';
      return;
    }
    if (this.s === 'LOCKED') {
      this.s = 'MENU';
      this.hooks.showMenu(true);
    }
  }

  /** The game shell shows a full-screen overlay (full-time EndScreen): release the cursor and swallow the
   *  unlock so no pause menu appears. Idempotent; closes any open pause menu first. */
  suspend(): void {
    if (this.s === 'SUSPENDED') return;
    if (this.s === 'MENU') this.hooks.showMenu(false);
    const wasLocked = this.s === 'LOCKED';
    this.s = 'SUSPENDED';
    if (wasLocked) this.hooks.exitLock(); // triggers lockChange(false), now swallowed by the SUSPENDED guard
  }

  /** The overlay closed (rematch started) — resume the normal click-to-capture flow. */
  unsuspend(): void {
    if (this.s === 'SUSPENDED') this.s = 'UNLOCKED';
  }

  /** The lock request failed (promise rejection or pointerlockerror) — degrade gracefully. */
  lockDenied(): void {
    if (this.s === 'UNLOCKED') this.s = 'FALLBACK';
  }

  /** ESC keydown. While LOCKED the browser exits the lock for us (lockChange handles the menu);
   *  while UNLOCKED/FALLBACK-but-playing ESC still reaches the menu. In MENU it toggles back. */
  esc(): void {
    if (this.s === 'MENU') {
      this.resume();
      return;
    }
    if ((this.s !== 'UNLOCKED' && this.s !== 'FALLBACK') || !this.hooks.isPlaying()) return;
    this.s = 'MENU';
    this.hooks.showMenu(true);
  }

  /** Continue: hide the menu + re-capture (the Continue click IS the required user gesture). */
  resume(): void {
    if (this.s !== 'MENU') return;
    this.hooks.showMenu(false);
    this.s = 'UNLOCKED';
    this.hooks.requestLock();
  }

  /** Quit: back to the main page. */
  quit(): void {
    if (this.s !== 'MENU') return;
    this.hooks.quit();
  }
}
